/** @jsxRuntime automatic */
/** @jsxImportSource react */
import type { ReactNode } from 'react';
import { Archive, Lock, Star, type LucideIcon } from 'lucide-react';
import type { RiskTier } from '../lib/api';
import type { Lifecycle } from '../lib/knowledge';
import type { OutfitBucket } from '../lib/outfitVisibility';
import { Icon } from './Icon';

export type Tone = 'ok' | 'warn' | 'bad' | 'info' | 'neutral' | 'accent';

export interface StatusChipProps {
  tone?: Tone;
  /** An icon in place of the dot. */
  icon?: LucideIcon;
  /** No dot (for a plain count or label). */
  plain?: boolean;
  children: ReactNode;
  title?: string;
  /** `status` for a chip that announces a save's outcome. */
  role?: 'status' | 'alert';
  className?: string;
}

/** Status is a chip with a dot or icon plus a word, never colour alone. */
export function StatusChip({ tone = 'neutral', icon, plain = false, children, title, role, className }: StatusChipProps) {
  return (
    <span className={['ui-chip', `ui-chip--${tone}`, className ?? ''].filter(Boolean).join(' ')} title={title} role={role}>
      {icon ? <Icon icon={icon} size="xs" /> : plain ? null : <span className="ui-chip-dot" aria-hidden="true" />}
      {children}
    </span>
  );
}

export interface ChipSpec {
  tone: Tone;
  label: string;
  icon?: LucideIcon;
}

/** The fixed tone maps (README section 2, "States"): the same thing always reads the same way on every page. */
export const RISK_CHIPS: Record<RiskTier, ChipSpec> = {
  standard: { tone: 'neutral', label: 'Standard' },
  sensitive: { tone: 'warn', label: 'Sensitive' },
  financial: { tone: 'info', label: 'Financial' },
  security: { tone: 'bad', label: 'Security' },
  'break-glass': { tone: 'bad', label: 'Break-glass', icon: Lock },
};

export const LIFECYCLE_CHIPS: Record<Lifecycle, ChipSpec> = {
  active: { tone: 'ok', label: 'Active' },
  negotiating: { tone: 'info', label: 'Negotiating' },
  completed: { tone: 'warn', label: 'Completed' },
  removal_requested: { tone: 'bad', label: 'Removal requested' },
  removed: { tone: 'neutral', label: 'Removed' },
};

/** Who can pick an outfit now (lib/outfitVisibility.ts): the gallery's filter words and its group chips. */
export const OUTFIT_CHIPS: Record<OutfitBucket | 'featured' | 'retired', ChipSpec> = {
  everyone: { tone: 'ok', label: 'Everyone now' },
  principals: { tone: 'neutral', label: 'Principals only' },
  scheduled: { tone: 'info', label: 'Scheduled' },
  ended: { tone: 'neutral', label: 'Ended' },
  featured: { tone: 'warn', label: 'Featured', icon: Star },
  retired: { tone: 'neutral', label: 'Retired', icon: Archive },
};

/** A chip from one of the tone maps. */
export function SpecChip({ spec }: { spec: ChipSpec }) {
  return <StatusChip tone={spec.tone} icon={spec.icon}>{spec.label}</StatusChip>;
}
